import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { AuditEvents } from "../components/AuditEvents";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { History, X } from "lucide-react";

const ALL = "__all__";

export default function AuditTrail() {
  const { clientId } = useParams();
  const [actor, setActor] = useState("");
  const [eventType, setEventType] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-head", clientId],
    queryFn: async () => (await api.get(`/clients/${clientId}`)).data,
  });

  const { data: filters } = useQuery({
    queryKey: ["audit-filters", clientId],
    queryFn: async () => (await api.get(`/clients/${clientId}/audit/filters`)).data,
    enabled: !!clientId,
  });

  if (isLoading)
    return (
      <Layout>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-96 rounded-md" />
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
          {error.response?.status === 403 ? "You do not have access to this customer." : "Failed to load audit trail."}
        </div>
      </Layout>
    );

  const client = data.client;
  const hasFilters = !!(actor || eventType);

  return (
    <Layout>
      <Breadcrumbs
        items={[
          { label: "Portfolio", to: "/" },
          { label: client.client_name, to: `/clients/${clientId}` },
          { label: "Audit Trail" },
        ]}
      />
      <div className="mt-4 flex items-center gap-3 border-b border-border pb-5">
        <div className="grid h-11 w-11 place-items-center rounded bg-secondary">
          <History size={22} />
        </div>
        <div>
          <h1 className="font-heading text-4xl font-black">Audit Trail</h1>
          <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
            {client.client_name} · who changed what
          </p>
        </div>
      </div>

      <p className="mt-4 mb-3 text-sm text-muted-foreground max-w-2xl">
        Every practitioner and system change captured from the provider audit extract. Click any event
        to see the exact field-level before/after values behind a variance.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="audit-filter-bar">
        <Select value={actor || ALL} onValueChange={(v) => setActor(v === ALL ? "" : v)}>
          <SelectTrigger data-testid="audit-actor-filter" className="w-full sm:w-64">
            <SelectValue placeholder="All actors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All actors</SelectItem>
            {(filters?.actors || []).map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventType || ALL} onValueChange={(v) => setEventType(v === ALL ? "" : v)}>
          <SelectTrigger data-testid="audit-type-filter" className="w-full sm:w-52">
            <SelectValue placeholder="All change types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All change types</SelectItem>
            {(filters?.event_types || []).map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <button
            data-testid="audit-clear-filters"
            onClick={() => { setActor(""); setEventType(""); }}
            className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
          >
            <X size={14} /> Clear
          </button>
        )}
      </div>

      <AuditEvents
        clientId={clientId}
        limit={300}
        actor={actor || undefined}
        eventType={eventType || undefined}
        emptyTitle={hasFilters ? "No audit events match these filters" : "No audit events loaded for this customer yet"}
        emptyText={hasFilters
          ? "Try clearing the actor or change-type filter to see more events."
          : "Audit data hasn't been ingested for this customer's payroll runs. Nothing has been lost — once the provider's audit extract is loaded, every practitioner and system change will appear here."}
      />
    </Layout>
  );
}
